import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import { AxnCompany, AxnRelyingParty } from "@idw-types/data/reference-data/axn";
import { WorkflowGeneralValidationErrors } from "@idw-types/common/validation/workflow-details-validation-errors";
import { FormattedRpServiceConfiguration } from "@idw-types/data/rp-service-configuration";
import { FormattedWorkflowDetailsConfiguration } from "@idw-types/data/workflow-details-configuration";
import { ThemingSubTabContainer } from "@containers/common/theming-sub-tab-container/theming-sub-tab-container";
import { BasicInfoTabContainer } from "./basic-info-tab-container/basic-info-tab-container";
import { FormattedIdwProfile } from "@idw-types/data/idw-profile";
import { AdvancedTabContainer } from "./advanced-tab-container/advanced-tab-container";
import { AxnDocumentationLink } from "@idw-types/data/reference-data/axn/axn-documentation-link";
import { TranslationTabContainer } from "./translation-tab-container/translation-tab-container";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import {
    MultiLanguageWrapper,
    AXNUIContent,
    DCUIContent,
    AXNUILayout
} from "@iddataweb/idw-common-ui/types";
import { isAxnAdminUser } from "@utils/permissions-util";
import { useSelector } from "react-redux";
import { RedirectUrlInformation } from "@idw-types/redirect-url-information";
import { getSessionState } from "@redux/selectors/root-selectors";

type WorkflowDetailsGeneralTabProps = {
    isDisabled: boolean;
    validationErrors: WorkflowGeneralValidationErrors;
    axnCompanies: Array<AxnCompany>;
    axnRelyingParties: Array<AxnRelyingParty>;
    primaryRpService: FormattedRpServiceConfiguration;
    rpServiceConfigurations: Array<FormattedRpServiceConfiguration>;
    workflowProfile: FormattedIdwProfile;
    workflowDetailsConfigurationState: FormattedWorkflowDetailsConfiguration;
    axnDocumentationLinks: Array<AxnDocumentationLink>;
    multiLanguageAXNUIContent: MultiLanguageWrapper<AXNUIContent>;
    multiLanguageDCUIContent: MultiLanguageWrapper<DCUIContent>;
    axnuiLayout: AXNUILayout;
    availableLanguages: LocaleTypes[];
    isMultiLanguageEnabled: boolean;
    workflowProfileKey: string;
    showIncodeWidget: boolean;
    redirectUrlsInformation: RedirectUrlInformation;
    setAvailableLanguages: (availableLanguages: Array<LocaleTypes>) => void;
    setWorkflowProfile: (profile: FormattedIdwProfile) => void;
    setRpServiceConfigurationsState: (state: Array<FormattedRpServiceConfiguration>) => void;
    setWorkflowDetailsConfigurationState: (state: FormattedWorkflowDetailsConfiguration) => void;
    setIsMultiLanguageEnabled: (isEnabled: boolean) => void;
    updateProperties: (properties: Array<{ propertyTypeId: string, newValue: string, added: boolean }>) => void;
    updateRpServicesAXNUIAndDCUIContentProperty: (availableLanguages: LocaleTypes[], isMultiLanguageEnabled: boolean) => void;
};

export const WorkflowDetailsGeneralTab = (props: WorkflowDetailsGeneralTabProps) => {
    const {
        isDisabled,
        validationErrors,
        axnCompanies,
        axnRelyingParties,
        primaryRpService,
        rpServiceConfigurations,
        workflowProfile,
        workflowDetailsConfigurationState,
        axnDocumentationLinks,
        multiLanguageAXNUIContent,
        multiLanguageDCUIContent,
        axnuiLayout,
        availableLanguages,
        isMultiLanguageEnabled,
        workflowProfileKey,
        showIncodeWidget,
        redirectUrlsInformation,
        setWorkflowProfile,
        setRpServiceConfigurationsState,
        setWorkflowDetailsConfigurationState,
        setAvailableLanguages,
        setIsMultiLanguageEnabled,
        updateProperties,
        updateRpServicesAXNUIAndDCUIContentProperty
    } = props;

    const { session } = useSelector(getSessionState);
    const isAdminUser = isAxnAdminUser(session.user);

    return (
        <ThemingSubTabContainer>
            <Tabs id="general" unmountOnExit={true}>
                <Tab eventKey={"general-basic-info"} title={validationErrors.basicInfo.tabTitle}>
                    <BasicInfoTabContainer
                        isDisabled={isDisabled}
                        validationErrors={validationErrors.basicInfo}
                        axnCompanies={axnCompanies}
                        axnRelyingParties={axnRelyingParties}
                        rpServiceConfigurations={rpServiceConfigurations}
                        primaryRpService={primaryRpService}
                        workflowProfile={workflowProfile}
                        workflowProfileKey={workflowProfileKey}
                        workflowDetailsConfigurationState={workflowDetailsConfigurationState}
                        isMultiLanguageEnabled={isMultiLanguageEnabled}
                        availableLanguages={availableLanguages}
                        redirectUrlsInformation={redirectUrlsInformation}
                        isAdminUser={isAdminUser}
                        setRpServiceConfigurationsState={setRpServiceConfigurationsState}
                        setWorkflowDetailsConfigurationState={setWorkflowDetailsConfigurationState}
                        setWorkflowProfile={setWorkflowProfile}
                    />
                </Tab>
                {workflowDetailsConfigurationState.permissions.canViewNonRestrictedProperties && (
                    <Tab eventKey={"general-advanced"} title={validationErrors.advanced.tabTitle}>
                        <AdvancedTabContainer
                            axnDocumentationLinks={axnDocumentationLinks}
                            isDisabled={isDisabled}
                            isAdminUser={isAdminUser}
                            validationErrors={validationErrors.advanced}
                            workflowDetailsConfigurationState={workflowDetailsConfigurationState}
                            rpServiceConfigurations={rpServiceConfigurations}
                            primaryRpService={primaryRpService}
                            showIncodeWidget={showIncodeWidget}
                            updateProperties={updateProperties}
                        />
                    </Tab>
                )}

                {
                    isAdminUser && <Tab eventKey={"translation"} title={validationErrors.translation.tabTitle}>
                        <TranslationTabContainer
                            isDisabled={isDisabled}
                            validationErrors={validationErrors.translation}
                            multiLanguageAXNUIContent={multiLanguageAXNUIContent}
                            multiLanguageDCUIContent={multiLanguageDCUIContent}
                            availableLanguages={availableLanguages}
                            isMultiLanguageEnabled={isMultiLanguageEnabled}
                            workflowProfile={workflowProfile}
                            axnDocumentationLinks={axnDocumentationLinks}
                            axnuiLayout={axnuiLayout}
                            setAvailableLanguages={setAvailableLanguages}
                            setWorkflowProfile={setWorkflowProfile}
                            setIsMultiLanguageEnabled={setIsMultiLanguageEnabled}
                            updateRpServicesAXNUIAndDCUIContentProperty={updateRpServicesAXNUIAndDCUIContentProperty}
                        />
                    </Tab>
                }
            </Tabs>
        </ThemingSubTabContainer>
    );
};